import { useState } from "react";
import { I } from "./icons";
import { targetArea } from "../domain/exerciseMeta";
import type { Exercise } from "../domain/types";

interface Props {
  ex: Exercise;
  /** e.g. "30 s / side" or "12" */
  dose: string;
  /** 0..1 completion, or undefined to hide the tick entirely */
  frac?: number;
  hipSafe: boolean;
  onOpen?: (ex: Exercise) => void;
  /** the move's Hindi name under the English one: the session overview when the player is in
   *  Hindi (ADR-055) */
  hi?: boolean;
}

/** One row of a session listing: icon, name, dose, completion tick, and the expandable
 *  "how / common mistakes / form video" detail from the original. */
export function ExerciseRow({ ex, dose, frac, hipSafe, onOpen, hi }: Props) {
  const [open, setOpen] = useState(false);
  const pct = frac === undefined ? null : frac;
  const cls = pct === null ? "" : pct >= 1 ? "full" : pct > 0 ? "part" : "";
  const [main, side] = dose.split(" / ");

  return (
    <div className={`ex${open ? " open" : ""}`} data-ex={ex.id}>
      <div
        className="hd"
        onClick={() => (onOpen ? onOpen(ex) : setOpen((v) => !v))}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen ? onOpen(ex) : setOpen((v) => !v);
          }
        }}
      >
        <div className={`ic ic-${ex.t}`}>{I[ex.t]}</div>
        <div className="nm">
          {ex.n}
          {ex.hip && hipSafe ? <span className="hip">watch hip</span> : null}
          {hi && ex.hi ? <small lang="hi">{ex.hi}</small> : null}
          <small>
            {ex.w}
            {targetArea(ex.id) ? ` · ${targetArea(ex.id)}` : ""}
          </small>
        </div>
        <div className="dose">
          {main}
          {side ? <small>{side}</small> : null}
        </div>
        {pct === null ? null : (
          <div
            className={`chk ${cls}`}
            style={{ ["--p" as string]: `${Math.round(pct * 100)}%` }}
          >
            {I.check}
          </div>
        )}
      </div>
      <div className="det">
        <p>{ex.how}</p>
        <b style={{ fontSize: ".85rem" }}>Common mistakes</b>
        <ul>
          {ex.wrong.map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
        <a href={ex.yt} target="_blank" rel="noopener noreferrer">
          Form video {I.ext}
        </a>
      </div>
    </div>
  );
}
